import { FC, ReactNode, useEffect, useState } from 'react';
import { ChargerType } from '@models';
import { NumberUtils, Tr, TypeUtils } from '@utils';
import { IconButton, Stack, StackProps, Typography } from '@mui/material';
import { FormikNumber } from '@components/common';
import { LocationChargerImage } from '../LocationChargerImage';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';

export interface ILocationChargerCardProps extends Omit<StackProps, 'title'> {
	title?: ReactNode;
	editable?: boolean;
	values: Record<ChargerType, number>;
	images?: Partial<Record<ChargerType, string>>;
	powerPerCharger?: number;
	deltaPower?: number;
	onEdit?: (data: ILocationChargerCardProps['values']) => void;
}

export const LocationChargerCard: FC<ILocationChargerCardProps> = ({
	title,
	values,
	images,
	powerPerCharger,
	deltaPower,
	editable,
	sx,
	onEdit,
	...props
}) => {
	const [isEditing, setIsEditing] = useState(false);
	const [userInput, setUserInput] = useState(values);
	const onEditOrSaveIconClick = () => {
		isEditing && onEdit?.(userInput);
		setIsEditing(!isEditing);
	};

	useEffect(() => setUserInput(values), [values]);

	return (
		<Stack direction="column" sx={{ padding: 4, height: 1.0, ...sx }} justifyContent="space-between" {...props}>
			<Stack direction="column" spacing={6}>
				<Stack direction="row" spacing={2} justifyContent="space-between">
					<Typography sx={{ fontWeight: 600, minHeight: 48 }}>{title || ''}</Typography>
					{editable ? (
						<IconButton
							size="small"
							sx={{ backgroundColor: 'primary.200', color: 'primary.main', width: 32, height: 32 }}
							onClick={onEditOrSaveIconClick}
						>
							{isEditing ? (
								<SaveIcon color="inherit" sx={{ width: 20, height: 20 }} />
							) : (
								<EditIcon color="inherit" sx={{ width: 20, height: 20 }} />
							)}
						</IconButton>
					) : null}
				</Stack>
				<Stack direction="row" spacing={1} justifyContent="space-between">
					{Object.values(ChargerType).map((type) => (
						<Stack
							key={`charger-card-${type}`}
							direction="column"
							alignItems="center"
							sx={{ maxWidth: '33%', minWidth: '33%' }}
						>
							<LocationChargerImage type={images?.[type] ? 'image' : type} url={images?.[type]} />
							<Typography sx={{ marginTop: 5 }}>{type}</Typography>
							{isEditing ? (
								<FormikNumber
									name=""
									value={userInput[type]}
									withoutFormik
									size="small"
									onChange={(e) => setUserInput({ ...userInput, [type]: +e.target.value })}
									sx={{ marginTop: 1.5 }}
								/>
							) : (
								<Typography sx={{ marginTop: 2.5, fontWeight: 600 }}>
									{NumberUtils.formatAsNumber(values?.[type] || 0, { decimalScale: 0 })}
								</Typography>
							)}
						</Stack>
					))}
				</Stack>
			</Stack>
			<Stack direction="column" sx={{ marginTop: 'auto' }} spacing={2}>
				{TypeUtils.isDefined(powerPerCharger) && (
					<Stack direction="row" spacing={2} justifyContent="space-between">
						<Typography sx={{ fontWeight: 600 }}>
							<Tr.Portal path="location.site-infra.power-per-charger" />
						</Typography>
						<Typography sx={{ fontWeight: 600 }}>
							{NumberUtils.formatAsNumber(powerPerCharger, { suffix: 'kW', decimalScale: 0 })}
						</Typography>
					</Stack>
				)}
				{TypeUtils.isDefined(deltaPower) && (
					<Stack direction="row" spacing={2} justifyContent="space-between">
						<Typography sx={{ fontWeight: 600 }}>
							<Tr.Portal path="location.site-infra.delta-power" />
						</Typography>
						<Typography sx={{ fontWeight: 600 }}>
							{NumberUtils.formatAsNumber(deltaPower, { suffix: 'kW', decimalScale: 0 })}
						</Typography>
					</Stack>
				)}
			</Stack>
		</Stack>
	);
};
